import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

// Single source of truth for follow-up urgency/badge/format logic, shared by
// every surface that shows a follow-up (Kanban card, Today's Follow-ups,
// Contact Details, Conversation sidebar, Calendar) so they never drift out
// of sync.
const FOLLOW_UP_BADGE = {
  overdue: { labelKey: 'FOLLOW_UP.BADGE.OVERDUE', color: 'ruby' },
  today: { labelKey: 'FOLLOW_UP.BADGE.DUE_TODAY', color: 'amber' },
  upcoming: { labelKey: 'FOLLOW_UP.BADGE.ON_TRACK', color: 'slate' },
};
const MEETING_BADGE = {
  labelKey: 'FOLLOW_UP.BADGE.MEETING_SCHEDULED',
  color: 'iris',
};

export function getFollowUpUrgency(nextFollowUpAt) {
  if (!nextFollowUpAt) return null;
  const followUpDate = new Date(nextFollowUpAt);
  const today = new Date();
  followUpDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  if (followUpDate < today) return 'overdue';
  if (followUpDate.getTime() === today.getTime()) return 'today';
  return 'upcoming';
}

export function getFollowUpBadge(nextFollowUpAt) {
  const urgency = getFollowUpUrgency(nextFollowUpAt);
  return urgency ? FOLLOW_UP_BADGE[urgency] : null;
}

// Meeting Scheduled takes visual precedence over plain urgency — the one
// place a follow-up's status color depends on more than just its date.
// Used by the Calendar (event chips, agenda rows); Kanban/Today's-Follow-ups
// intentionally keep the urgency-only badge above, unchanged from the
// Follow-up milestone.
export function getEventStatusBadge(nextFollowUpAt, isMeeting = false) {
  if (isMeeting) return MEETING_BADGE;
  return getFollowUpBadge(nextFollowUpAt);
}

export function formatFollowUpDate(nextFollowUpAt) {
  return nextFollowUpAt
    ? new Date(nextFollowUpAt).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : '';
}

export function formatFollowUpTime(nextFollowUpAt) {
  return nextFollowUpAt
    ? new Date(nextFollowUpAt).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
}

export function useFollowUp() {
  const store = useStore();
  const { t } = useI18n();
  const isSaving = ref(false);

  // The backend resolves "which conversation" (the contact's last one)
  // itself from the agent id alone — the frontend never sends or needs a
  // conversation id.
  const saveFollowUp = async ({
    contactId,
    assignedAgentId,
    nextFollowUpAt,
    followUpNote,
    meetingRequired,
    meetingProvider,
    meetingUrl,
    meetingReference,
    ccEmails,
    // New follow-up fields. Left undefined by callers that predate follow-up types so their
    // payload is unchanged; the backend derives a type for those (Nexceo::FollowUps::Snapshot).
    followUpType,
    followUpStatus,
    meetingLocation,
    followUpTeamId,
    cancellationReason,
  }) => {
    isSaving.value = true;
    try {
      await store.dispatch('contacts/update', {
        id: contactId,
        ...(assignedAgentId ? { assignedAgentId } : {}),
        customAttributes: {
          // Stored as a `date`-typed custom attribute — the Calendar (and
          // any other date-range filter) casts this to ::date in SQL, and
          // Postgres raises on `''::date` for *any* row it evaluates, not
          // just this contact's. null keeps a cleared follow-up queryable;
          // '' silently breaks the whole account's Calendar.
          next_follow_up_at: nextFollowUpAt || null,
          follow_up_note: followUpNote || '',
          meeting_required: !!meetingRequired,
          meeting_provider: meetingProvider || '',
          meeting_url: meetingUrl || '',
          meeting_reference: meetingReference || '',
          // Server (Contact#normalize_cc_emails/validate_cc_emails) trims,
          // lowercases, dedupes, and validates — this only needs to send
          // whatever the chip input currently holds.
          cc_emails: ccEmails || [],
          ...(followUpType !== undefined
            ? { follow_up_type: followUpType }
            : {}),
          ...(followUpStatus !== undefined
            ? { follow_up_status: followUpStatus }
            : {}),
          ...(meetingLocation !== undefined
            ? { meeting_location: meetingLocation || '' }
            : {}),
          ...(followUpTeamId !== undefined
            ? { follow_up_team_id: followUpTeamId || null }
            : {}),
          ...(cancellationReason !== undefined
            ? { cancellation_reason: cancellationReason || null }
            : {}),
        },
      });
      useAlert(t('FOLLOW_UP.SAVE_SUCCESS'));
      return true;
    } catch (error) {
      useAlert(error?.response?.data?.message || t('FOLLOW_UP.SAVE_ERROR'));
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  // `completed` (not `cancelled`): finishing a follow-up never notifies the customer.
  const completeFollowUp = contactId =>
    saveFollowUp({
      contactId,
      nextFollowUpAt: '',
      followUpNote: '',
      meetingRequired: false,
      meetingProvider: '',
      meetingUrl: '',
      meetingReference: '',
      ccEmails: [],
      followUpType: '',
      followUpStatus: 'completed',
      meetingLocation: '',
      followUpTeamId: null,
    });

  // A meeting that will not happen (as opposed to one that was done): the explicit `cancelled`
  // status is what makes the backend notify the customer and staff.
  const cancelFollowUp = (contactId, cancellationReason = '') =>
    saveFollowUp({
      contactId,
      nextFollowUpAt: '',
      followUpNote: '',
      meetingRequired: false,
      meetingProvider: '',
      meetingUrl: '',
      meetingReference: '',
      ccEmails: [],
      followUpType: '',
      followUpStatus: 'cancelled',
      meetingLocation: '',
      followUpTeamId: null,
      cancellationReason,
    });

  return {
    isSaving,
    saveFollowUp,
    completeFollowUp,
    cancelFollowUp,
  };
}
